import React, { useEffect } from 'react';
import { Form } from 'antd';
import { Checkbox, Icon, Input, Spinner } from 'ui';
import { Dayjs } from 'dayjs';
import DiscountType from '../../../enums/discount-type';
import useQueryApiClient from 'utils/useQueryApiClient';
import { useCoupon } from './hooks/use-coupon';
import { useCategories } from './hooks/use-categories';
import { useProducts } from './hooks/use-product';
import { useTranslations } from './hooks/use-translations';
import { useAuth } from '../../../hooks/useAuth';
import CategorySelectField from './components/CategorySelectField';
import DiscountField from './components/DiscountField';
import ProductSelectField from './components/ProductSelectField';
import PeriodField from './components/PeriodField';
import CouponDrawerFooter from './components/CouponDrawerFooter';
import { StyledCouponCreateUpdateDrawer, StyledForm } from './style';

export interface CouponFormValues {
  name: string;
  code: string;
  discount_value: number;
  discount_type: DiscountType;
  category_ids: number[];
  product_ids: number[];
  period: [Dayjs, Dayjs];
  active: boolean;
}

interface Props {
  id?: number;
  isVisible: boolean;
  onClose: Function;
}

const CouponCreateUpdateDrawer = ({ id, isVisible, onClose }: Props) => {
  const t = useTranslations();
  const [form] = Form.useForm<CouponFormValues>();
  const { user } = useAuth();
  const farmId = user?.farmId;

  const couponQuery = useCoupon(id, (data) => {
    form.setFieldsValue({
      ...data,
      period: [data.start_date, data.end_date],
    });
  });
  const categoriesQuery = useCategories();
  const productsQuery = useProducts();

  useEffect(() => {
    if (!id) form.resetFields();
  }, [id, form]);

  const innerOnClose = () => {
    form.resetFields();
    onClose();
  };

  const { appendData: SubmitCoupon } = useQueryApiClient({
    request: {
      url: id
        ? `api/v2/farms/${farmId}/coupons/${id}`
        : `api/v2/farms/${farmId}/coupons`,
      method: id ? 'PATCH' : 'POST',
    },
    onSuccess: () => onClose(),
  });

  const onFinish = (values: CouponFormValues) => {
    const [startDay, endDay] = values.period || [undefined, undefined];
    const toUtcMidnight = (d: Dayjs) => {
      return d ? d.format('YYYY-MM-DD') + 'T00:00:00.000Z' : null;
    };

    const start_date = toUtcMidnight(startDay);
    const end_date = toUtcMidnight(endDay);
    const active = values.active === true;
    SubmitCoupon({ ...values, start_date, end_date, active });
  };

  return (
    <StyledCouponCreateUpdateDrawer
      open={isVisible}
      onClose={innerOnClose}
      title={t(id ? 'general.edit' : 'coupons.create')}
      footer={
        <CouponDrawerFooter
          id={id}
          onClose={innerOnClose}
          onSubmit={form.submit}
        />
      }
      closeIcon={<Icon name={'global-close-icon'} />}
    >
      <StyledForm form={form} onFinish={onFinish} layout={'vertical'}>
        <Spinner spinning={couponQuery.isLoading}>
          <Form form={form} onFinish={onFinish} layout="vertical">
            <Checkbox name={'active'} label={t('coupons.table.status')} />
            <Input
              name="name"
              label={t('coupons.table.name')}
              validations={'required'}
            />
            <Input
              name="code"
              label={t('coupons.table.coupon')}
              validations={'required'}
            />
            <DiscountField />
            <CategorySelectField categories={categoriesQuery.data} />
            <ProductSelectField products={productsQuery.data} />
            <PeriodField />
          </Form>
        </Spinner>
      </StyledForm>
    </StyledCouponCreateUpdateDrawer>
  );
};

export default CouponCreateUpdateDrawer;
